import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes, OrganizationTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationInvitationDefinitionCacheKey,
    getOrganizationInvitationDefinitionsCacheKey,
    longTermCacheStaleTime,
    onUpdateOrganizationInvitationDefinitions,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationDefinition', {
    invitationsDefinitionsLoadFailure: 'Failed to load invitation templates',
    createInvitationDefinitionSuccess: 'Invitation template created',
    createInvitationDefinitionFailure: 'Failed to create invitation template',
    updateInvitationDefinitionSuccess: 'Invitation template updated',
    updateInvitationDefinitionFailure: 'Failed to update invitation template',
    deleteInvitationDefinitionSuccess: 'Invitation template deleted',
    deleteInvitationDefinitionFailure: 'Failed to delete invitation template',
    deleteInvitationDefinitionsSuccess: 'Invitation templates deleted',
    deleteInvitationDefinitionsFailure: 'Failed to delete invitation templates',
    invitationDefinitionLoadFailure: 'Failed to load invitation template',
    loadInvitationDefinitionFileFailure: 'Failed to load invitation file',
});

export type UseOrganizationInvitationDefinitionsArgs = {
    onCreateInvitationDefinition?: () => void;
    onUpdateInvitationDefinitionSuccess?: () => void;
    onDeleteInvitationDefinitionSuccess?: () => void;
    onDeleteInvitationDefinitionsSuccess?: () => void;
    invitationDefinitionId?: string;
    onFetchedFileDownloadUrl?: (downloadUrl: string) => void;
};

export type UpdateArgs = {
    invitationDefinitionId: string;
    invitationDefinition: APITypes.organizationService.UpdateInvitationDefinitionRequest;
};

export type GetDownloadUrlArgs = {
    invitationDefinitionId: string;
    fileId: string;
};

export type UseOrganizationInvitationDefinitionsReturnValue = {
    invitationDefinitions: APITypes.organizationService.ListDefinitionsResponse['invitationDefinitions'];
    isLoadingInvitationDefinitions: boolean;
    isFetchingInvitationDefinitions: boolean;
    createInvitationDefinition: (
        invitationDefinition: APITypes.organizationService.CreateInvitationDefinitionRequest,
    ) => void;
    isCreatingInvitationDefinition: boolean;
    updateInvitationDefinition: (args: UpdateArgs) => void;
    isUpdatingInvitationDefinition: boolean;
    deleteInvitationDefinition: (invitationDefinitionId: string) => void;
    isDeletingInvitationDefinition: boolean;
    deleteInvitationDefinitions: (invitationDefinitionIds: string[]) => void;
    isDeletingInvitationDefinitions: boolean;
    invitationDefinition: APITypes.organizationService.GetInvitationDefinitionResponse | null;
    isLoadingInvitationDefinition: boolean;
    getDownloadUrl: (args: GetDownloadUrlArgs) => void;
    isLoadingDownloadUrl: boolean;
};

export const isConsentCheckboxInput = (
    input: OrganizationTypes.InvitationDefinitionInput,
): input is OrganizationTypes.InvitationDefinitionInputConsentCheckbox => {
    return input.type === 'consent-checkbox';
};

export const isFileInput = (
    input: OrganizationTypes.InvitationDefinitionInput,
): input is OrganizationTypes.InvitationDefinitionInputFile => {
    return input.type === 'file';
};

export default function useOrganizationInvitationDefinitions({
    onCreateInvitationDefinition,
    onUpdateInvitationDefinitionSuccess,
    onDeleteInvitationDefinitionSuccess,
    onDeleteInvitationDefinitionsSuccess,
    onFetchedFileDownloadUrl,
    invitationDefinitionId,
}: UseOrganizationInvitationDefinitionsArgs): UseOrganizationInvitationDefinitionsReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const invitationDefinitionsQuery = useQuery({
        queryKey: getOrganizationInvitationDefinitionsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListDefinitionsResponse>(
                urls.api.organizations.invitationDefinitions.invitationDefinitions(organizationId),
            );
        },
    });
    useQueryErrorNotify(invitationDefinitionsQuery, intl('invitationsDefinitionsLoadFailure'));

    const invitationDefinitionQuery = useQuery({
        queryKey: invitationDefinitionId ? getOrganizationInvitationDefinitionCacheKey(invitationDefinitionId) : [],
        queryFn: async () => {
            const value = await appAPI.get<APITypes.organizationService.GetInvitationDefinitionResponse>(
                urls.api.organizations.invitationDefinitions.invitationDefinition(
                    organizationId,
                    invitationDefinitionId!,
                ),
            );

            return value;
        },
        staleTime: longTermCacheStaleTime,
        enabled: !!invitationDefinitionId,
    });
    useQueryErrorNotify(invitationDefinitionQuery, intl('invitationDefinitionLoadFailure'));

    const createOrganizationInvitationDefinitionMutation = useMutation({
        mutationFn: async (invitationDefinition: APITypes.organizationService.CreateInvitationDefinitionRequest) => {
            return await appAPI.post(
                urls.api.organizations.invitationDefinitions.invitationDefinitions(organizationId),
                {
                    body: invitationDefinition,
                },
            );
        },
        onSuccess: () => {
            onUpdateOrganizationInvitationDefinitions.resetCache({})();
            notify('success', intl('createInvitationDefinitionSuccess'));
            onCreateInvitationDefinition?.();
        },
        onError: () => {
            notify('error', intl('createInvitationDefinitionFailure'));
        },
    });

    const updateInvitationDefinitionMutation = useMutation({
        mutationFn: async (args: UpdateArgs) => {
            return await appAPI.put(
                urls.api.organizations.invitationDefinitions.invitationDefinition(
                    organizationId,
                    args.invitationDefinitionId,
                ),
                {
                    body: args.invitationDefinition,
                },
            );
        },
        onSuccess: () => {
            onUpdateOrganizationInvitationDefinitions.resetCache({})();
            notify('success', intl('updateInvitationDefinitionSuccess'));
            onUpdateInvitationDefinitionSuccess?.();
        },
        onError: () => {
            notify('error', intl('updateInvitationDefinitionFailure'));
        },
    });

    const deleteInvitationDefinition = useMutation({
        mutationFn: async (invitationDefinitionId: string) => {
            return await appAPI.del(
                urls.api.organizations.invitationDefinitions.invitationDefinition(
                    organizationId,
                    invitationDefinitionId,
                ),
            );
        },
        onSuccess: () => {
            onUpdateOrganizationInvitationDefinitions.resetCache({})();
            notify('success', intl('deleteInvitationDefinitionSuccess'));
            onDeleteInvitationDefinitionSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationDefinitionFailure'));
        },
    });

    const deleteInvitationDefinitionsMutation = useMutation({
        mutationFn: async (invitationDefinitionIds: string[]) => {
            for (const invitationDefinitionId of invitationDefinitionIds) {
                await appAPI.del(
                    urls.api.organizations.invitationDefinitions.invitationDefinition(
                        organizationId,
                        invitationDefinitionId,
                    ),
                );
            }
        },
        onSuccess: () => {
            onUpdateOrganizationInvitationDefinitions.resetCache({})();
            notify('success', intl('deleteInvitationDefinitionsSuccess'));
            onDeleteInvitationDefinitionsSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationDefinitionsFailure'));
        },
    });

    const getDownloadUrlMutation = useMutation({
        mutationFn: async (args: GetDownloadUrlArgs) => {
            const { invitationDefinitionId, fileId } = args;
            const response =
                await appAPI.get<APITypes.organizationService.GetInvitationDefinitionFileDownloadUrlResponse>(
                    urls.api.organizations.invitationDefinitions.invitationDefinitionFile(
                        organizationId,
                        invitationDefinitionId,
                        fileId,
                    ),
                );

            return response.downloadUrl;
        },
        onSuccess: (data) => {
            onFetchedFileDownloadUrl?.(data);
        },
        onError: () => {
            notify('error', intl('loadInvitationDefinitionFileFailure'));
        },
    });

    return {
        invitationDefinitions: invitationDefinitionsQuery.data?.invitationDefinitions || [],
        isLoadingInvitationDefinitions: invitationDefinitionsQuery.isLoading,
        isFetchingInvitationDefinitions: invitationDefinitionsQuery.isFetching,
        createInvitationDefinition: createOrganizationInvitationDefinitionMutation.mutate,
        isCreatingInvitationDefinition: createOrganizationInvitationDefinitionMutation.isPending,
        updateInvitationDefinition: updateInvitationDefinitionMutation.mutate,
        isUpdatingInvitationDefinition: updateInvitationDefinitionMutation.isPending,
        deleteInvitationDefinition: deleteInvitationDefinition.mutate,
        isDeletingInvitationDefinition: deleteInvitationDefinition.isPending,
        deleteInvitationDefinitions: deleteInvitationDefinitionsMutation.mutate,
        isDeletingInvitationDefinitions: deleteInvitationDefinitionsMutation.isPending,
        invitationDefinition: invitationDefinitionQuery.data || null,
        isLoadingInvitationDefinition: invitationDefinitionQuery.isLoading,
        getDownloadUrl: getDownloadUrlMutation.mutate,
        isLoadingDownloadUrl: getDownloadUrlMutation.isPending,
    };
}
